import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useCanonicalUrl } from '../components/seo/SchemaMarkup';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing ninjathlete.com, purchasing products, or using our tools and calculators, you agree to be bound by these Terms of Service. If you do not agree, please discontinue use of the site.',
  },
  {
    title: '2. Products & Orders',
    body: 'All product prices are listed in USD. We reserve the right to refuse or cancel any order, including orders with pricing errors or suspected fraud. Orders are confirmed by email once payment is processed through our payment provider (Stripe). Title and risk of loss pass to you upon carrier delivery.',
  },
  {
    title: '3. Shipping & Returns',
    body: 'US orders ship via USPS with standard (5-7 business days) and express (2-3 business days) options. Returns are accepted within 30 days of delivery — see our Returns Policy for full details and per-product exceptions.',
  },
  {
    title: '4. Educational Content Disclaimer',
    body: 'All articles, guides, calculators, and tools on NinjAthlete are for educational and informational purposes only. They are not medical advice, and they do not create a doctor-patient relationship. Peptide-related content covers research compounds — consult a licensed physician before beginning any protocol, supplement, or training program.',
  },
  {
    title: '5. Affiliate Disclosure',
    body: 'Some links on this site are affiliate links (including American Peptide Research). We may earn a commission on qualifying purchases at no additional cost to you. Affiliate relationships never influence our editorial conclusions — see our Editorial Policy.',
  },
  {
    title: '6. Rewards Program',
    body: 'NinjAthlete points are promotional credits with no cash value. Points may be earned through engagement and redeemed for store discounts. We reserve the right to adjust earn rates, redemption values, or revoke points obtained through abuse.',
  },
  {
    title: '7. Intellectual Property',
    body: 'All site content — articles, branding, imagery, tools, and code — is owned by NinjAthlete or its licensors. You may share excerpts with attribution and a link back; wholesale reproduction is prohibited without written permission.',
  },
  {
    title: '8. Agentic & AI Purchases',
    body: 'Purchases initiated through AI shopping agents (e.g., ChatGPT Instant Checkout via the Agentic Commerce Protocol) are subject to these same terms. The buyer named on the order is responsible for the purchase as if placed directly on the site.',
  },
  {
    title: '9. Limitation of Liability',
    body: 'To the maximum extent permitted by law, NinjAthlete is not liable for indirect, incidental, or consequential damages arising from use of the site, its content, or products purchased. Our total liability is limited to the amount you paid for the product giving rise to the claim.',
  },
  {
    title: '10. Changes & Contact',
    body: 'We may update these terms at any time; continued use constitutes acceptance. Questions? Email info@ninjathlete.com.',
  },
];

export default function TermsPage() {
  useCanonicalUrl();
  return (
    <main className="pt-20 min-h-screen bg-black text-white" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8" data-testid="terms-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <h1 className="text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-3">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-12">Last updated: June 12, 2026 · NinjAthlete, Dallas, TX</p>
        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-lg font-bold mb-2 text-red-500">{s.title}</h2>
              <p className="text-gray-300 leading-relaxed text-sm">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-14 pt-8 border-t border-gray-800 text-sm text-gray-400">
          Related: <Link to="/privacy" className="text-red-400 hover:text-red-300 underline">Privacy Policy</Link>
          {' · '}<Link to="/returns" className="text-red-400 hover:text-red-300 underline">Returns</Link>
          {' · '}<Link to="/shipping" className="text-red-400 hover:text-red-300 underline">Shipping</Link>
          {' · '}<Link to="/editorial-policy" className="text-red-400 hover:text-red-300 underline">Editorial Policy</Link>
        </div>
      </div>
    </main>
  );
}
